import { useEffect, useState } from 'react';
import { TrendingUp, Plus } from 'lucide-react';
import { recordAthleteMetric, listAthleteMetricHistory, type AthleteMetric } from '@tss/shared';
import { Button, Input, Select } from '@tss/shared/ui';

interface GrowthTrackingSectionProps {
  athleteId: string;
  /** Called after a new measurement lands so the parent can refresh user
   *  profile state (so the dashboard header shows the new height/weight). */
  onMeasurementRecorded?: () => void;
}

const METRIC_OPTIONS = [
  { value: 'height', label: 'Height' },
  { value: 'weight', label: 'Weight' },
] as const;

const UNIT_OPTIONS_BY_METRIC: Record<'height' | 'weight', { value: string; label: string }[]> = {
  height: [
    { value: 'in', label: 'inches' },
    { value: 'cm', label: 'cm' },
  ],
  weight: [
    { value: 'lbs', label: 'lbs' },
    { value: 'kg', label: 'kg' },
  ],
};

type MetricType = 'height' | 'weight';

export function GrowthTrackingSection({
  athleteId,
  onMeasurementRecorded,
}: GrowthTrackingSectionProps) {
  const [metrics, setMetrics] = useState<AthleteMetric[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = () => {
    setError(null);
    listAthleteMetricHistory(athleteId, { limit: 100 })
      .then((res) => setMetrics(res.metrics))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load history'));
  };

  useEffect(() => {
    refresh();
    // refresh whenever the athleteId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  const heightHistory = metrics?.filter((m) => m.metric_type === 'height') ?? [];
  const weightHistory = metrics?.filter((m) => m.metric_type === 'weight') ?? [];

  return (
    <section
      className="card"
      style={{
        padding: '20px 22px',
        marginTop: '24px',
      }}
      aria-label="Growth tracking"
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '6px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={18} style={{ color: 'var(--purple-600)' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)', margin: 0 }}>
            Growth tracking
          </h3>
        </div>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Plus size={14} />}
          onClick={() => setShowForm((s) => !s)}
        >
          {showForm ? 'Cancel' : 'Record measurement'}
        </Button>
      </header>
      <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: 0, marginBottom: '14px' }}>
        Update every few months so parents and coaches can see your growth. Latest values are
        reflected on your profile.
      </p>

      {showForm && (
        <RecordMeasurementForm
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            refresh();
            onMeasurementRecorded?.();
          }}
        />
      )}

      {error && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
          {error}
        </p>
      )}

      {metrics === null && !error && (
        <p style={{ color: 'var(--gray-500)', fontSize: '13px' }}>Loading…</p>
      )}

      {metrics !== null && metrics.length === 0 && (
        <p style={{ color: 'var(--gray-500)', fontSize: '13px', margin: 0 }}>
          No measurements yet. Tap "Record measurement" to add your first one.
        </p>
      )}

      {metrics !== null && metrics.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginTop: '12px',
          }}
        >
          <MetricSummary label="Height" history={heightHistory} />
          <MetricSummary label="Weight" history={weightHistory} />
        </div>
      )}
    </section>
  );
}

// ─── Recording form ──────────────────────────────────────────────────────────

function RecordMeasurementForm({
  onSuccess,
  onClose,
}: {
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [metricType, setMetricType] = useState<MetricType>('height');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<string>(UNIT_OPTIONS_BY_METRIC.height[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // When the user switches metric type, reset unit to that metric's first option
  function handleMetricChange(next: MetricType) {
    setMetricType(next);
    setUnit(UNIT_OPTIONS_BY_METRIC[next][0].value);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setFormError('Enter a value.');
      return;
    }
    setSubmitting(true);
    try {
      await recordAthleteMetric({ metric_type: metricType, value: trimmed, unit });
      onSuccess();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save measurement');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '12px',
        alignItems: 'end',
        padding: '14px',
        background: 'var(--gray-50)',
        borderRadius: 'var(--radius-md)',
        marginBottom: '14px',
      }}
    >
      <div>
        <label
          style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}
        >
          Metric
        </label>
        <Select
          value={metricType}
          onValueChange={(v) => handleMetricChange(v as MetricType)}
          options={METRIC_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
      </div>
      <div>
        <label
          style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}
        >
          Value
        </label>
        <Input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={metricType === 'height' ? '6\'2"' : '185'}
          required
        />
      </div>
      <div>
        <label
          style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}
        >
          Unit
        </label>
        <Select
          value={unit}
          onValueChange={(v) => setUnit(v)}
          options={UNIT_OPTIONS_BY_METRIC[metricType]}
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" loading={submitting}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </div>
      {formError && (
        <p
          role="alert"
          style={{
            gridColumn: '1 / -1',
            color: 'var(--color-danger)',
            fontSize: '12px',
            margin: 0,
          }}
        >
          {formError}
        </p>
      )}
    </form>
  );
}

// ─── Per-metric summary card with mini chart ─────────────────────────────────

function MetricSummary({ label, history }: { label: string; history: AthleteMetric[] }) {
  if (history.length === 0) {
    return (
      <div
        style={{ padding: '14px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}
      >
        <p
          style={{
            fontSize: '12px',
            color: 'var(--gray-500)',
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {label}
        </p>
        <p style={{ fontSize: '14px', color: 'var(--gray-500)', margin: '6px 0 0' }}>
          No history yet.
        </p>
      </div>
    );
  }

  // history is newest-first from the API; render oldest-first for the chart
  const chronological = [...history].reverse();
  const latest = history[0];
  const oldest = chronological[0];

  return (
    <div
      style={{ padding: '14px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}
    >
      <p
        style={{
          fontSize: '12px',
          color: 'var(--gray-500)',
          margin: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px' }}>
        <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--gray-900)' }}>
          {latest.value}
        </span>
        {latest.unit && (
          <span style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{latest.unit}</span>
        )}
      </div>
      <p style={{ fontSize: '11px', color: 'var(--gray-500)', margin: '4px 0 8px' }}>
        First on {oldest.recorded_at.slice(0, 10)} · {history.length} entries
      </p>
      <SparkLine points={chronological} />
    </div>
  );
}

// Tiny inline SVG sparkline. Falls back to dots when values aren't numeric.
function SparkLine({ points }: { points: AthleteMetric[] }) {
  if (points.length < 2) return null;
  // Try to extract a leading number from each value (e.g. "185 lbs" → 185, "6'2\"" → 6)
  const nums = points.map((p) => {
    const m = p.value.match(/[\d.]+/);
    return m ? parseFloat(m[0]) : NaN;
  });
  if (nums.some((n) => Number.isNaN(n))) return null;

  const width = 220;
  const height = 36;
  const pad = 2;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || 1;

  const xs = (i: number) => pad + (i * (width - pad * 2)) / (nums.length - 1);
  const ys = (v: number) => height - pad - ((v - min) * (height - pad * 2)) / range;

  const path = nums
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${xs(i).toFixed(1)} ${ys(v).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Trend, ${nums.length} data points from ${min} to ${max}`}
      style={{ display: 'block' }}
    >
      <path d={path} fill="none" stroke="var(--purple-600)" strokeWidth="2" />
    </svg>
  );
}
